/**
 * Messaging Context — B-2 / M4 (Retired /api/conversations Surface)
 *
 * Owner Audit §4 Batch B-2 mandate: retire ALL active frontend calls
 * to `/api/conversations` (server returns HTTP 410 GONE under
 * Security Surface Lock S1). The canonical thread authority is
 * `/api/messaging/threads` (see `services/MessagingAPI.js`), consumed
 * by V2 pages (`PatientMessagesV2`, `ClinicMessagesV2`).
 *
 * This legacy context is still IMPORTED by a small number of pages
 * (`PatientMessages.jsx`, `ClinicRepresentativeDashboard.jsx`,
 * `admin/SalesRepMessaging.jsx`) — none of them are the primary
 * messaging surface anymore. To keep the shape stable while
 * eliminating 410 traffic and refetch loops, every API-touching
 * method here is now a no-op: state stays empty, helpers return
 * empty arrays / zero counts. Consumers still render, but no
 * `/api/conversations` request ever leaves the browser.
 *
 * BOOT-01 — `useAuthGate` is kept as the canonical auth-boundary
 * import and the initial `useEffect` early-returns on `!authorized`.
 * The gate no longer guards a fetch (there is none), but the boundary
 * is preserved so any future re-introduction of a canonical fetch
 * cannot re-open the pre-auth request storm.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';

const MessagingContext = createContext();

export const useMessaging = () => {
  const ctx = useContext(MessagingContext);
  if (!ctx) throw new Error('useMessaging must be used within MessagingProvider');
  return ctx;
};

export const MessagingProvider = ({ children }) => {
  const [conversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [loading] = useState(false);

  const fetchConversations = useCallback(async () => {
    // B-2 / M4 — retired surface. No-op.
    return;
  }, []);

  // BOOT-01 — canonical auth gate preserved; still early-returns on
  // !authorized so a future fetch reintroduction cannot bypass it.
  const { authorized: _msg_authorized } = useAuthGate();
  useEffect(() => {
    if (!_msg_authorized) return;
    // B-2 / M4 — intentional no-op. See file header.
  }, [_msg_authorized]);

  const getPatientConversations = useCallback(() => [], []);
  const getCoordinatorConversations = useCallback(() => [], []);
  const getClinicConversations = useCallback(() => [], []);

  const sendMessage = useCallback(async () => null, []);
  const createConversation = useCallback(async () => null, []);
  const markAsRead = useCallback(async () => {}, []);
  const selectConversation = useCallback(async () => {}, []);

  const getUnreadCount = useCallback(() => 0, []);
  const getTotalUnreadForCoordinator = useCallback(() => 0, []);

  return (
    <MessagingContext.Provider value={{
      conversations,
      activeConversation,
      loading,
      fetchConversations,
      getPatientConversations,
      getCoordinatorConversations,
      getClinicConversations,
      sendMessage,
      createConversation,
      markAsRead,
      selectConversation,
      getUnreadCount,
      getTotalUnreadForCoordinator,
      setActiveConversation,
    }}>
      {children}
    </MessagingContext.Provider>
  );
};

export default MessagingContext;
